import { BehaviorSubject, combineLatest, Subscription } from 'rxjs';
import { Injectable, OnDestroy } from '@angular/core';
import { DeveloperService } from '../developer/developer.service';
import { DeveloperHardwareConfig } from '@app/store/settings/DeveloperHardwareConfig';
import { Messgeraete } from '@interfaces/interfaces';
import { tap } from 'rxjs/operators';

/**
 * Electron meldet die konfigurierten Geräte über das window.GspDeviceApi Objekt.
 */
export interface GspDeviceApi {
	// VitaPen (TappingTest)
	hasVitaPen: boolean;
	// VitaReact (Taster)
	hasVitaReact: boolean;
	// Allgemeine Touchpanel Verfügbarkeit
	hasTouchPanel: boolean;
	// Fahradergometer
	hasExternalBicycleErgometer: boolean,
	// Lautsprecher/Soundkarten Kombination
	soundDevice: string;
	// externes Audiometer
	hasExternalAudiometry: boolean;

	getSystemVolume(): Promise<number>;
	setSystemVolume(volume: number): Promise<void>;
}

export interface GspDeviceApiProvider {
	GspDeviceApi?: GspDeviceApi;
}

export interface AudioElementWithSinkId extends HTMLAudioElement {
	setSinkId: (sinkId: string) => Promise<undefined>;
}

@Injectable()
export class MessplatzService implements OnDestroy {

	private readonly _connectedDevices: Messgeraete[];
	private _availableDevices: Messgeraete[];
	public readonly hardware$: BehaviorSubject<Messgeraete[]> = new BehaviorSubject<Messgeraete[]>([]);

	private subscription = new Subscription();
	private allowHardwareRewriting: boolean;
	private hardwareConfig: DeveloperHardwareConfig;

	constructor(
		private devSettings: DeveloperService,
	) {
		this._connectedDevices = this.receiveConnectedDevices();
		this._availableDevices = [...this._connectedDevices];

		const devDependencies = combineLatest([
			devSettings.isDemoMode$.pipe(
				tap(isDemoMode => this.allowHardwareRewriting = isDemoMode)
			),
			devSettings.hardwareConfig$.pipe(
				tap(config => this.hardwareConfig = config)
			)
		]);
		this.subscription.add(devDependencies.subscribe(() => this.setDevicesFromEmulated()));
	}

	private receiveConnectedDevices(): Messgeraete[] {
		const provider = window as unknown as GspDeviceApiProvider;
		const devices: Messgeraete[] = [];

		if (provider.GspDeviceApi) {
			if (provider.GspDeviceApi.hasVitaPen) {
				devices.push(Messgeraete.TappingPen);
			}
			if (provider.GspDeviceApi.hasVitaReact) {
				devices.push(Messgeraete.ButtonDevice);
			}
			if (provider.GspDeviceApi.hasTouchPanel) {
				devices.push(Messgeraete.TouchScreen);
			}
			if (provider.GspDeviceApi.hasExternalBicycleErgometer) {
				devices.push(Messgeraete.ExternalBicycleErgometer);
			}
			if (provider.GspDeviceApi.hasExternalAudiometry) {
				devices.push(Messgeraete.ExternalAudiometry);
			}
			if (provider.GspDeviceApi.soundDevice) {
				devices.push(Messgeraete.SoundDevice);
			}
			switch (provider.GspDeviceApi.soundDevice) {
				case 'Audiometry_Sh_HDA_300__Sb_X_G1__Th_PS_TPY_2003_KBB':
					devices.push(Messgeraete.Audiometry_Sh_HDA_300__Sb_X_G1__Th_PS_TPY_2003_KBB);
					devices.push(Messgeraete.CalibratedHeadphones);
					break;
				case 'Audiometry_bd_DT_770_PRO_32__Sb_X_G1':
					devices.push(Messgeraete.Audiometry_bd_DT_770_PRO_32__Sb_X_G1);
					devices.push(Messgeraete.CalibratedHeadphones);
					break;
				default:
					break;
			}
		}
		return devices;
	}

	public getConnectedSoundDevices(): Messgeraete[] {
		return this._availableDevices.filter(device => device === Messgeraete.Audiometry_Sh_HDA_300__Sb_X_G1__Th_PS_TPY_2003_KBB
			|| device === Messgeraete.Audiometry_bd_DT_770_PRO_32__Sb_X_G1);
	}

	private setDevicesFromEmulated(): void {
		if (!this.allowHardwareRewriting || !this.hardwareConfig?.overrideHardwareStatus) {
			this._availableDevices = [...this._connectedDevices];
			this.hardware$.next(this._availableDevices);
			return;
		}

		const emulatedDevices = [] as Messgeraete[];
		if (this.hardwareConfig.emulateTouchScreen) {
			emulatedDevices.push(Messgeraete.TouchScreen);
		}
		if (this.hardwareConfig.emulateTappingPen) {
			emulatedDevices.push(Messgeraete.TappingPen);
		}
		if (this.hardwareConfig.emulateButtonDevice) {
			emulatedDevices.push(Messgeraete.ButtonDevice);
		}
		if (this.hardwareConfig.emulateExternalAudiometry) {
			emulatedDevices.push(Messgeraete.ExternalAudiometry);
		}
		if (this.hardwareConfig.emulateSoundDevice) {
			emulatedDevices.push(Messgeraete.SoundDevice);
		}
		if (this.hardwareConfig.emulateAudiometry_Sh_HDA_300__Sb_X_G1__Th_PS_TPY_2003_KBB) {
			emulatedDevices.push(Messgeraete.Audiometry_Sh_HDA_300__Sb_X_G1__Th_PS_TPY_2003_KBB);
			emulatedDevices.push(Messgeraete.CalibratedHeadphones);
			if (!this.hardwareConfig.emulateSoundDevice) {
				emulatedDevices.push(Messgeraete.SoundDevice);
			}
		}
		if (this.hardwareConfig.emulateAudiometry_bd_DT_770_PRO_32__Sb_X_G1) {
			emulatedDevices.push(Messgeraete.Audiometry_bd_DT_770_PRO_32__Sb_X_G1);
			emulatedDevices.push(Messgeraete.CalibratedHeadphones);
			if (!this.hardwareConfig.emulateSoundDevice) {
				emulatedDevices.push(Messgeraete.SoundDevice);
			}
		}
		if (this.hardwareConfig.emulateTestHardware) {
			emulatedDevices.push(Messgeraete.TestHardware);
		}


		if (this.hardwareConfig.emulateExternalBicycleErgometer) {
			emulatedDevices.push(Messgeraete.ExternalBicycleErgometer);
		}

		this._availableDevices = emulatedDevices;
		this.hardware$.next(this._availableDevices);
	}

	public hasAvailableDevice(device: Messgeraete): boolean {
		return this._availableDevices.includes(device);
	}

	public getMissingDevices(requiredDevices: Messgeraete[]): Messgeraete[] {
		if (!requiredDevices || requiredDevices.length < 1) {
			return [];
		}
		return requiredDevices.filter(device => !this._availableDevices.includes(device));
	}

	public hasAllDevices(requiredDevices: Messgeraete[]): boolean {
		return !this.getMissingDevices(requiredDevices).length;
	}

	public getMissingDevicesNames(requiredDevices: Messgeraete[]): string[] {
		return this.getMissingDevices(requiredDevices).map( md => md.toString() );
	}

	public hasDevices(): boolean {
		return this._availableDevices.length > 0;
	}

	public async setCalibratedHeadphonesSinkId(audioElement: AudioElementWithSinkId): Promise<AudioElementWithSinkId> {
		const connectedSoundDevices = this.getConnectedSoundDevices();
		if (connectedSoundDevices.length === 1 && navigator.mediaDevices?.enumerateDevices && audioElement.setSinkId) {
			let label: string;
			switch (connectedSoundDevices[0]) {
				case Messgeraete.Audiometry_bd_DT_770_PRO_32__Sb_X_G1:
				case Messgeraete.Audiometry_Sh_HDA_300__Sb_X_G1__Th_PS_TPY_2003_KBB:
					label = 'Sound BlasterX G1';
					break;
			}
			const mediaDevices = await navigator.mediaDevices.enumerateDevices();
			const outputDevice = mediaDevices
				.filter(device => device.kind === 'audiooutput' && device.deviceId !== 'communications')
				.find(info => info.label.includes(label));
			if (outputDevice && outputDevice.deviceId) {
				await audioElement.setSinkId(outputDevice.deviceId);
			}
		}
		return audioElement;
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}
}
